import { memo, useCallback } from 'react';
import { useAtomValue } from 'jotai';
import type { FileContainerId, GroupId, ModelId, ViewModelId } from 'balus-common';
import type { SelectedItemSet, Size } from '@view-model/models/common/basic';
import type { PositionDelta } from '@view-model/models/common/types/ui';
import type { ModelElementId } from '@view-model/domain/key';
import type { DragContext } from '@model-framework/ui';
import { downloadFile } from '@framework/utils/downloadFile';
import type { FileContainer } from '../domain/FileContainer';
import type { FileContainerCollectionContents } from '../domain/FileContainerCollectionContents';
import { uploadingFileContainersAtomFamily } from '../adapter/uploadingFileContainersAtom';
import { FileContainerView } from './FileContainerView';
import { FileCard } from './FileCard';

type Props = {
  groupId: GroupId;
  viewModelId: ViewModelId;
  modelId: ModelId;
  fileContainerContents: FileContainerCollectionContents;
  selectedItems: SelectedItemSet<ModelElementId>;
  showCreatedUser: boolean;
  readonly: boolean;
  onClick(id: FileContainerId, e: React.MouseEvent<SVGGElement>): void;
  onDragStart(id: FileContainerId, e: React.MouseEvent<SVGGElement>): void;
  onDrag(context: DragContext): void;
  onDragEnd(context: DragContext): void;
  onDelete(id: FileContainerId): void;
  onResize(id: FileContainerId, positionDelta: PositionDelta, size: Size): void;
};

export const FileContainerCollectionView = memo(
  ({
    groupId,
    viewModelId,
    modelId,
    fileContainerContents,
    selectedItems,
    showCreatedUser,
    readonly,
    onClick,
    onDragStart,
    onDrag,
    onDragEnd,
    onDelete,
    onResize,
  }: Props) => {
    const uploadingFileContainers = useAtomValue(uploadingFileContainersAtomFamily(modelId));

    const handleDownload = useCallback(
      (fileContainer: FileContainer) =>
        downloadFile(fileContainer.downloadUrl(groupId, viewModelId, window.location.origin)),
      [groupId, viewModelId]
    );

    return (
      <>
        {fileContainerContents.allIds().map((id) => {
          const fileContainer = fileContainerContents.containers.get(id);
          const position = fileContainerContents.positions.find(id);
          if (!fileContainer || !position) return null;
          const isSelected = selectedItems.isSelected(id);

          return (
            <g key={id} data-model-element-id={id}>
              <FileContainerView
                groupId={groupId}
                viewModelId={viewModelId}
                fileContainer={fileContainer}
                position={position}
                isSelected={isSelected}
                showMenu={isSelected && selectedItems.isSingleSelected()}
                showCreatedUser={showCreatedUser}
                readonly={readonly}
                onClick={onClick}
                onDragStart={onDragStart}
                onDrag={onDrag}
                onDragEnd={onDragEnd}
                onDownload={handleDownload}
                onDelete={onDelete}
                onResize={onResize}
              />
            </g>
          );
        })}
        {uploadingFileContainers.map(({ id, filename, position, size }) => (
          <g key={id} transform={position.toSVGTranslate()}>
            <FileCard filename={filename} size={size} isSelected={false} isUploading />
          </g>
        ))}
      </>
    );
  }
);
FileContainerCollectionView.displayName = 'FileContainerCollectionView';
